import React, { useState, useEffect, useRef } from "react";
import axios from "axios";
import { BASE_URL } from "../../../env";
import Sidebar from "../../../components/layout/Sidebar";
import { Form, Row, Col, Button } from "react-bootstrap";
import { jsPDF } from "jspdf";
import html2canvas from "html2canvas";
import "../../members/screens/MemberPrint.css";
import {
  getCurrentDateTimeWithOffset,
  maskAadharNumber,
  getPendingEmiAmountForMember,
  getFutureEmiAmountWithMonths,
  getTotalEmiPaid,
  generateLoanSummaryForMember,
  formatISODateToLocalDMY,
  getStatusOfMember,
  getDisbursementAmount,
} from "../../../utils/utils";

const AccountStatementReport = () => {
  const ref = useRef();
  const [openSidebarToggle, setOpenSidebarToggle] = useState(window.innerWidth > 1000);
  const [cusApplicationId, setCusApplicationId] = useState("");
  const [memberDetails, setMemberDetails] = useState(null);
  const [validationError, setValidationError] = useState("");
  const [showStatement, setShowStatement] = useState(false);
  const [loading, setLoading] = useState(false);

  const OpenSidebar = () => {
    setOpenSidebarToggle(!openSidebarToggle);
  };

  const handleClickViewStatement = async () => {
    setValidationError("");
    setShowStatement(false);
    if (!cusApplicationId) {
      setValidationError("Please enter Prospect ID");
      return;
    }
    try {
      const response = await axios.get(
        `${BASE_URL}/api/getjlgaccountstatement/${cusApplicationId}`
      );
      if (response.data.message) {
        setMemberDetails(response.data.message);
        setShowStatement(true);
        // console.log("memberDetails: " + JSON.stringify(response.data.message));
      } else if (response.data.error) {
        setValidationError(response.data.error);
      }
    } catch (error) {
      console.log("Error fetching member details:", error);
      setValidationError(error.message || "An error occurred while fetching data");
    }
  };

  const handleDownloadStatement = async () => {
    setLoading(true);

    const input = ref.current;
    const pdf = new jsPDF("p", "mm", "a4");
    const originalPdfWidth = pdf.internal.pageSize.getWidth();
    const originalPdfHeight = pdf.internal.pageSize.getHeight();
    const footerHeight = 20; // Reserve space for the footer
    const pdfWidth = originalPdfWidth * 0.9;
    const pdfHeight = originalPdfHeight * 0.9 - footerHeight; // Adjusted height for content
    const xOffset = (originalPdfWidth - pdfWidth) / 2;
    const yOffset = (originalPdfHeight - pdfHeight - footerHeight) / 2; // Adjust for footer
    const sections = input.querySelectorAll(".section");

    let heightLeft = pdfHeight;

    for (let i = 0; i < sections.length; i++) {
      const section = sections[i];

      const canvas = await html2canvas(section, { useCORS: true, scale: 2 });
      const imgData = canvas.toDataURL("image/jpeg", 0.7);
      const imgProps = pdf.getImageProperties(imgData);
      const imgHeight = (imgProps.height * pdfWidth) / imgProps.width;

      if (
        heightLeft < imgHeight ||
        section.classList.contains("sanction-letter-section")
      ) {
        addFooter(pdf, originalPdfWidth, originalPdfHeight);
        pdf.addPage();
        heightLeft = pdfHeight;
      }

      pdf.addImage(
        imgData,
        "JPEG",
        xOffset,
        yOffset + (pdfHeight - heightLeft),
        pdfWidth,
        imgHeight
      );
      heightLeft -= imgHeight;

      // Add border to the current page after content is added
      pdf.rect(5, 5, originalPdfWidth - 10, originalPdfHeight - 10);
    }

    // Add footer to the last page
    addFooter(pdf, originalPdfWidth, originalPdfHeight);

    pdf.setProperties({
      title: "Document",
      subject: "Generated Document",
      author: "Your Name",
      keywords: "generated, javascript, web 2.0, ajax",
      creator: "Your App",
    });

    pdf.save(`${memberDetails?.memberDetails?.memberName || 'Member'}-Account Statement`);
    setLoading(false);
  };

  // Function to add footer with horizontal line and company details
  const addFooter = (pdf, width, height) => {
    const footerY = height - 15; // Set footer position above bottom edge

    // Draw horizontal line
    pdf.setLineWidth(0.3);
    pdf.line(10, footerY, width - 10, footerY);

    // Add company name and address
    pdf.setFontSize(6);
    pdf.setFont("helvetica", "bold");
    pdf.text("VETRIVIKAS CAPITAL TRUST", 10, footerY + 5);
    pdf.text("Registered Office:", 10, footerY + 8);
    pdf.setFont("helvetica", "normal");
    pdf.text(
      "Flat No.25,ARUNACHALESHWARAR 3RD CROSS STREET,HMS COLONY 1ST MAIN ROAD, KOKKALAPPI, MADURAI, TAMILNADU-625016. Office No: 0452-490 9438",
      10 + pdf.getTextWidth("Registered Office: "),
      footerY + 8
    );
  };

  // The whole statement is derived from one loan summary, exactly as the
  // Business Loan and GP Loan account statements do it.
  //
  // This screen used to work out the EMI dates itself with
  // getFirstAndLastEmiDates - the MONTHLY helper - passing
  // emiDateByBranchManager as the EMI day. A JLG loan has no per-member EMI
  // day; it repays on its centre's meeting day, so that field is always null.
  // The helper then returned null dates, the guard below fired, and the screen
  // printed Rs.0 paid on every JLG statement (bugfix.md Bug 21).
  //
  // generateLoanSummaryForMember picks the correct helper per product itself -
  // getJlgFirstAndLastEmiDates with the centre meeting day for JLG - so this
  // screen needs no EMI-date plumbing of its own. The far end date is safe:
  // principalPaid and interestPaid are built from actual receipts, never from
  // scheduled future EMIs.
  const getLoanSummary = () => {
    try {
      if (!memberDetails?.memberDetails) return null;

      // The JLG branch of the calculation reads the centre's bmMeetingDayOrder
      // WITHOUT optional chaining, so a member with no centre throws. Guarded
      // here rather than there, because an unguarded throw blanks the page
      // (bugfix.md Bug 15).
      if (!memberDetails.memberDetails.fk_member_details_belongsTo_center_centerId) {
        console.warn("Center data not available, using default values");
        return null;
      }

      return generateLoanSummaryForMember(
        memberDetails.memberDetails,
        "2000-01-01",
        "2099-12-31"
      );
    } catch (error) {
      console.error("Error calculating loan summary:", error);
      return null;
    }
  };

  // Safe function to get outstanding amounts with error handling
  const getOutstandingAmounts = () => {
    const summary = getLoanSummary();
    return {
      principalPaid: summary?.principalPaid || 0,
      interestPaid: summary?.interestPaid || 0,
    };
  };

  // Safe function to get installment period.
  // Takes its dates from the same loan summary. It previously used the monthly
  // helper too, which is why this line was blank on every JLG statement
  // (bugfix.md Bug 21).
  const getInstallmentPeriod = () => {
    try {
      const summary = getLoanSummary();
      if (!summary?.firstEmiDate || !summary?.lastEmiDate) return "";

      const first = new Date(summary.firstEmiDate);
      const last = new Date(summary.lastEmiDate);
      if (isNaN(first.getTime()) || isNaN(last.getTime())) return "";

      return (
        first.toLocaleDateString("en-US") + " to " + last.toLocaleDateString("en-US")
      );
    } catch (error) {
      console.error("Error getting installment period:", error);
      return "";
    }
  };

  const memberInfo = memberDetails?.memberDetails
    ? [
        {
          label: "Customer Name",
          value: memberDetails.memberDetails.memberName || "",
        },
        {
          label: "Address",
          value: `${memberDetails.memberDetails.permanentDoorNo || ""}, ${memberDetails.memberDetails.permanentStreetNo || ""}, ${memberDetails.memberDetails.permanentPanchayat || ""}, ${memberDetails.memberDetails.permanentTaluk || ""}, ${memberDetails.memberDetails.permanentDistrict || ""}-${memberDetails.memberDetails.permanentPincode || ""}`,
        },
        {
          label: "Agreement No.",
          value: memberDetails.memberDetails.ApplicationId || "",
        },
        {
          label: "Branch",
          value: memberDetails.branchName || "",
        },
        {
          label: "Loan Amount",
          value: memberDetails.memberDetails.sanctionedLoanAmountBySanctionCommittee || 0,
        },
        {
          label: "CKYC ID",
          value: memberDetails.memberDetails.aadharNo ? maskAadharNumber(memberDetails.memberDetails.aadharNo) : "",
        },
        {
          label: "Installment Overdue",
          value: (() => {
            try {
              return getPendingEmiAmountForMember(memberDetails.memberDetails) || 0;
            } catch (error) {
              console.error("Error getting pending EMI amount:", error);
              return 0;
            }
          })(),
        },
        {
          label: "Currency",
          value: "INR",
        },
        {
          label: "Future Installments",
          value: (() => {
            try {
              return getFutureEmiAmountWithMonths(memberDetails.memberDetails) || 0;
            } catch (error) {
              console.error("Error getting future EMI amount:", error);
              return 0;
            }
          })(),
        },
        {
          label: "Installment Paid",
          value: (() => {
            try {
              return getTotalEmiPaid(memberDetails.memberDetails) || 0;
            } catch (error) {
              console.error("Error getting total EMI paid:", error);
              return 0;
            }
          })(),
        },
        {
          label: "Principal Paid",
          value: getOutstandingAmounts().principalPaid,
        },
        {
          label: "Interest Paid",
          value: getOutstandingAmounts().interestPaid,
        },
        {
          label: "Monthly Reducing Date",
          value: "26%",
        },
        {
          label: "Sanction Date",
          value: memberDetails.memberDetails.branchManagerStatusUpdatedAt 
            ? formatISODateToLocalDMY(memberDetails.memberDetails.branchManagerStatusUpdatedAt)
            : "",
        },
        {
          label: "Status",
          value: (() => {
            try {
              return getStatusOfMember(memberDetails.memberDetails) || "";
            } catch (error) {
              console.error("Error getting member status:", error);
              return "";
            }
          })(),
        },
        {
          label: "Interest Rate Type",
          value: "Fixed",
        },
        {
          label: "Tenure (months)",
          value: memberDetails.memberDetails.proposedLoanDetails?.tenureInMonths || 0,
        },
        {
          label: "Frequency",
          value: "Monthly EMIs",
        },
        {
          label: "Installment Period",
          value: getInstallmentPeriod(),
        },
      ]
    : [];

  const accountStatementReport = memberDetails?.memberDetails
    ? [
        {
          receivedDate: memberDetails.memberDetails.branchManagerStatusUpdatedAt 
            ? formatISODateToLocalDMY(memberDetails.memberDetails.branchManagerStatusUpdatedAt)
            : "",
          emiDate: memberDetails.memberDetails.branchManagerStatusUpdatedAt 
            ? formatISODateToLocalDMY(memberDetails.memberDetails.branchManagerStatusUpdatedAt)
            : "",
          particulars: "Loan Amount",
          increasedBy: "",
          decreasedBy: memberDetails.memberDetails.sanctionedLoanAmountBySanctionCommittee || 0,
          otherDues: "",
        },
        {
          receivedDate: memberDetails.memberDetails.branchManagerStatusUpdatedAt 
            ? formatISODateToLocalDMY(memberDetails.memberDetails.branchManagerStatusUpdatedAt)
            : "",
          emiDate: memberDetails.memberDetails.branchManagerStatusUpdatedAt 
            ? formatISODateToLocalDMY(memberDetails.memberDetails.branchManagerStatusUpdatedAt)
            : "",
          particulars: "Life insurance premium Due",
          increasedBy: memberDetails.memberDetails.insuranceAmount || 0,
          decreasedBy: "",
          otherDues: "",
        },
        {
          receivedDate: memberDetails.memberDetails.branchManagerStatusUpdatedAt 
            ? formatISODateToLocalDMY(memberDetails.memberDetails.branchManagerStatusUpdatedAt)
            : "",
          emiDate: memberDetails.memberDetails.branchManagerStatusUpdatedAt 
            ? formatISODateToLocalDMY(memberDetails.memberDetails.branchManagerStatusUpdatedAt)
            : "",
          particulars: "Processing Fee",
          increasedBy: memberDetails.memberDetails.processingCharge || 0,
          decreasedBy: "",
          otherDues: "",
        },
        {
          receivedDate: memberDetails.memberDetails.branchManagerStatusUpdatedAt 
            ? formatISODateToLocalDMY(memberDetails.memberDetails.branchManagerStatusUpdatedAt)
            : "",
          emiDate: memberDetails.memberDetails.branchManagerStatusUpdatedAt 
            ? formatISODateToLocalDMY(memberDetails.memberDetails.branchManagerStatusUpdatedAt)
            : "",
          particulars: "GST",
          increasedBy: memberDetails.memberDetails.gst || 0,
          decreasedBy: "",
          otherDues: "",
        },
        {
          receivedDate: memberDetails.memberDetails.branchManagerStatusUpdatedAt 
            ? formatISODateToLocalDMY(memberDetails.memberDetails.branchManagerStatusUpdatedAt)
            : "",
          emiDate: memberDetails.memberDetails.branchManagerStatusUpdatedAt 
            ? formatISODateToLocalDMY(memberDetails.memberDetails.branchManagerStatusUpdatedAt)
            : "",
          particulars: "Security Deposit",
          increasedBy: memberDetails.memberDetails.securityDeposit || 0,
          decreasedBy: "",
          otherDues: "",
        },
        {
          receivedDate: memberDetails.memberDetails.transactionDate 
            ? formatISODateToLocalDMY(memberDetails.memberDetails.transactionDate)
            : "",
          emiDate: memberDetails.memberDetails.transactionDate 
            ? formatISODateToLocalDMY(memberDetails.memberDetails.transactionDate)
            : "",
          particulars: "Credit Loan Amount",
          // Was memberDetails.memberDetails.transactionAmount directly - the
          // Accounts Manager's own free-typed bank-transaction figure, with
          // no check against what the Sanction Committee actually approved.
          // A Rs.55,000 sanction typed as a Rs.50,000 transaction showed up
          // here unnoticed, while every EMI/interest/report figure for the
          // same loan kept using the sanctioned Rs.55,000 - the statement
          // and the charges disagreed. getDisbursementAmount is what
          // AccountStatementReport.js (BL) and GetAccountStatement.js (GPL)
          // already use for the equivalent line; JLG's own copy had drifted
          // from its two siblings (same shape as Bug 1 - see bugfix.md) and
          // is brought back in line here.
          increasedBy: getDisbursementAmount(memberDetails.memberDetails),
          decreasedBy: "",
          otherDues: "",
        },
      ]
    : [];

  const totalSumIncreasedBy = accountStatementReport.reduce(
    (sum, item) => sum + Number(item.increasedBy || 0),
    0
  );

  const totalSumDecreasedBy = accountStatementReport.reduce(
    (sum, item) => sum + Number(item.decreasedBy || 0),
    0
  );

  const totalSumReceivedAmount = memberDetails?.memberDetails?.receiptsDetails
    ? memberDetails.memberDetails.receiptsDetails.reduce(
        (sum, item) => sum + Number(item.receivedAmount || 0),
        0
      )
    : 0;

  return (
    <div className="dashboard-over">
      <div className="d-flex" style={{width: '100vw'}}>
        <div style={{width: '13%'}}>
          <Sidebar
            openSidebarToggle={openSidebarToggle}
            OpenSidebar={OpenSidebar}
          />
        </div>

        <div
          className="container-fluid"
          style={{
           overflowY: "scroll",
           width: '87%',
           height: "100vh",
           // scrollbarWidth: "none",
          }}
        >
          <h1>Account Statement Report</h1>
          
          <Row>
            <Col xs={12} lg={3}>
              <Form.Group>
                <Form.Control
                  type="text"
                  placeholder="Enter Prospect ID"
                  value={cusApplicationId}
                  onChange={(event) => setCusApplicationId(event.target.value)}
                />
              </Form.Group>
            </Col>
            <Col xs={12} lg={3}>
              <Form.Group>
                <Button onClick={handleClickViewStatement}>
                  View Statement
                </Button>
              </Form.Group>
            </Col>
            {showStatement && (
              <Col xs={12} lg={3}>
                <Form.Group>
                  <Button onClick={handleDownloadStatement} disabled={loading}>
                    {loading ? "Downloading..." : "Download PDF"}
                  </Button>
                </Form.Group>
              </Col>
            )}
            {validationError && <h3 className="text-danger mt-2">{validationError}</h3>}
          </Row>
          
          {showStatement && memberDetails && (
            <div ref={ref} className="print-container">
              <div className="section d-flex justify-content-end ">
                <img
                  src={require("../../../assets/vetrivikaslogo.jpeg")}
                  alt="logo"
                  className="printlogo"
                />
              </div>
              <div className="header section">
                <b>Account Statement</b>
              </div>
              <p className="section">
                <i>Generated On: {getCurrentDateTimeWithOffset()}</i>
              </p>
              <div>
                <table
                  className="table table-striped table-bordered"
                  style={{ border: "1px solid black" }}
                >
                  <tbody>
                    {memberInfo.map((item, index) => (
                      <tr key={index} className="section">
                        <th>{item.label}</th>
                        <td>{item.value}</td>
                      </tr>
                    ))}
                  </tbody>
                </table>
              </div>
              <div className="mt-5">
                <table
                  className="table table-striped table-bordered"
                  style={{ border: "1px solid black" }}
                >
                  <thead>
                    <tr className="section">
                      <th>Received Date</th>
                      <th>EMI Date</th>
                      <th>Particular</th>
                      <th>Increased By</th>
                      <th>Decreased By</th>
                      <th>Other Dues</th>
                    </tr>
                  </thead>
                  <tbody>
                    {accountStatementReport.map((item, index) => (
                      <tr key={index} className="section">
                        <td>{item.receivedDate}</td>
                        <td>{item.emiDate}</td>
                        <td>{item.particulars}</td>
                        <td>{item.increasedBy}</td>
                        <td>{item.decreasedBy}</td>
                        <td>{item.otherDues}</td>
                      </tr>
                    ))}
                    {memberDetails.memberDetails.receiptsDetails && 
                      [...memberDetails.memberDetails.receiptsDetails]
                        .sort((a, b) => new Date(a.emiDate) - new Date(b.emiDate))
                        .map((item, index) => (
                          <React.Fragment key={`receipt-${index}`}>
                            <tr className="section">
                              <td></td>
                              <td>{item.emiDate ? formatISODateToLocalDMY(item.emiDate) : ""}</td>
                              <td>Due for Installment {index + 1}</td>
                              <td>{item.receivedAmount || 0}</td>
                              <td></td>
                              <td></td>
                            </tr>
                            <tr className="section">
                              <td>{item.emiDate ? formatISODateToLocalDMY(item.emiDate) : ""}</td>
                              <td></td>
                              <td>Payment Received From Cash</td>
                              <td></td>
                              <td>{item.receivedAmount || 0}</td>
                              <td></td>
                            </tr>
                          </React.Fragment>
                        ))
                    }
                    <tr className="section">
                      <th></th>
                      <th></th>
                      <th>Total: </th>
                      <th>{totalSumIncreasedBy + totalSumReceivedAmount}</th>
                      <th>{totalSumDecreasedBy + totalSumReceivedAmount}</th>
                      <th></th>
                    </tr>
                  </tbody>
                </table>
              </div>
              <div className="section">
                <p>
                  This is a computer generated statement and does not require
                  any signature.
                </p>
                <h3 className="text-center">*** END OF REPORT ***</h3>
                <p>
                  DISCLAIMER: This is a system generated Account Statement and
                  does not require signature. In case of any discrepancy in this
                  Account Statement, please call 0452-490 9438 or visit nearest
                  Vetrivikas Capital Trust branch within 10 (TEN) days from the
                  date of last entry made in this statement, failing which, this
                  Account Statement will be deemed to be accepted by you (the
                  borrower).
                </p>
              </div>
            </div>
          )}
        </div>
      </div>
    </div>
  );
};

export default AccountStatementReport;